import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHead from "../components/PageHead";
import RunPipelineButton from "../components/RunPipelineButton";
import AddInstrument from "../components/AddInstrument";
import { actionStyle, confColor, fmt } from "../lib/signals";
import WinRateBadge from "../components/WinRateBadge";
import { MagnifyingGlass, Plus, SortAscending } from "@phosphor-icons/react";

const ACTIONS = ["ALL", "LONG", "SHORT", "HOLD", "WATCH", "EXIT"];
const TFS = ["ALL", "Intraday", "BTST", "Swing (3-5d)", "Positional (1-2w)", "Investment (1m+)"];

function sentPill(s) {
  const sc = s?.score || 0;
  const cls = sc > 25 ? "text-bull bg-bull/10" : sc < -25 ? "text-bear bg-bear/10" : "text-neutral bg-neutral/10";
  const lbl = sc > 25 ? "Bull" : sc < -25 ? "Bear" : "Neut";
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>{lbl} {s?.mentions || 0}</span>;
}

export default function HotList() {
  const [rows, setRows] = useState(null);
  const [action, setAction] = useState("ALL");
  const [tf, setTf] = useState("ALL");
  const [minConf, setMinConf] = useState(0);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("latest");
  const [showAdd, setShowAdd] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    const params = { sort };
    if (action !== "ALL") params.action = action;
    if (tf !== "ALL") params.timeframe = tf;
    if (minConf) params.min_confluence = minConf;
    api.get("/hotlist", { params }).then((r) => setRows(r.data.hotlist)).catch(() => setRows([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action, tf, minConf, sort]);
  useEffect(() => { const t = setInterval(load, 15000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [action, tf, minConf, sort]);

  const filtered = useMemo(() => (rows || []).filter((r) => r.ticker.toLowerCase().includes(q.toLowerCase())), [rows, q]);

  return (
    <>
      <PageHead title="Signal Hot List" subtitle="Group calls confirmed by the Bratz engine, ranked by conviction"
        right={<div className="flex items-center gap-2">
          <button data-testid="open-add-instrument" onClick={() => setShowAdd(true)}
            className="glass rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2 hover:bg-foreground/5 transition-colors duration-200">
            <Plus size={15} weight="bold" /> Track Instrument
          </button>
          <RunPipelineButton onDone={load} />
        </div>} />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2 glass rounded-full px-3">
          <MagnifyingGlass size={15} className="text-muted-foreground" />
          <input data-testid="hotlist-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter ticker…"
            className="bg-transparent py-2 text-sm outline-none w-36" />
        </div>
        <div data-testid="filter-action" className="flex glass rounded-full p-1">
          {ACTIONS.map((o) => (
            <button key={o} data-testid={`filter-action-${o}`} onClick={() => setAction(o)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${action === o ? "brand-gradient text-white" : "text-muted-foreground hover:text-foreground"}`}>{o}</button>
          ))}
        </div>
        <select data-testid="filter-timeframe" value={tf} onChange={(e) => setTf(e.target.value)}
          className="glass rounded-full px-4 py-2 text-xs outline-none">
          {TFS.map((t) => <option key={t} value={t}>{t === "ALL" ? "All timeframes" : t}</option>)}
        </select>
        <div data-testid="sort-toggle" className="flex glass rounded-full p-1">
          {[["latest", "Latest"], ["conviction", "Conviction"]].map(([v, l]) => (
            <button key={v} data-testid={`sort-${v}`} onClick={() => setSort(v)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 transition-all duration-200 ${sort === v ? "brand-gradient text-white" : "text-muted-foreground hover:text-foreground"}`}>
              {v === "latest" && <SortAscending size={12} weight="bold" />}{l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground glass rounded-full px-4 py-2">
          <span>Min conviction</span>
          <input data-testid="filter-confluence" type="range" min="0" max="80" step="5" value={minConf}
            onChange={(e) => setMinConf(Number(e.target.value))} className="accent-brand" />
          <span className="font-mono text-foreground w-6">{minConf}</span>
        </div>
        <span className="ml-auto text-xs text-muted-foreground font-mono">{rows === null ? "…" : `${filtered.length} results`}</span>
      </div>

      <div className="glass-strong rounded-3xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1140px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-left font-semibold px-3 py-3 pl-6">Ticker</th>
                <th className="text-left font-semibold px-3 py-3">Action</th>
                <th className="text-left font-semibold px-3 py-3">TA</th>
                <th className="text-left font-semibold px-3 py-3">Group</th>
                <th className="text-left font-semibold px-3 py-3">Src Win</th>
                <th className="text-left font-semibold px-3 py-3">Sentiment</th>
                <th className="text-right font-semibold px-3 py-3">Price</th>
                <th className="text-right font-semibold px-3 py-3">Entry</th>
                <th className="text-right font-semibold px-3 py-3">Stop</th>
                <th className="text-right font-semibold px-3 py-3">Target 1</th>
                <th className="text-right font-semibold px-3 py-3">Conviction</th>
                <th className="text-left font-semibold px-3 py-3 pr-6">Timeframe</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((h) => (
                <tr key={h.id} data-testid={`signal-row-${h.ticker}`} onClick={() => navigate(`/signal/${h.id}`)}
                  className="border-b border-border/50 hover:bg-foreground/5 cursor-pointer transition-colors duration-150">
                  <td className="px-3 py-3 pl-6 font-semibold whitespace-nowrap">{h.ticker} <span className="text-muted-foreground text-xs font-normal">{h.exchange}</span>{h.on_watchlist && <span className="ml-1.5 text-[9px] text-brand">★</span>}</td>
                  <td className="px-3 py-3"><span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold border ${actionStyle(h.action)}`}>{h.action}</span></td>
                  <td className="px-3 py-3 text-muted-foreground text-xs">{h.ta_available ? h.ta_decision : "no data"}</td>
                  <td className="px-3 py-3"><span className={`text-xs font-medium ${h.group_bias === "BULLISH" ? "text-bull" : h.group_bias === "BEARISH" ? "text-bear" : "text-muted-foreground"}`}>{h.group_bias}</span></td>
                  <td className="px-3 py-3"><WinRateBadge value={h.source_win_rate} /></td>
                  <td className="px-3 py-3">{sentPill(h.sentiment)}</td>
                  <td className="px-3 py-3 text-right font-mono">{fmt(h.price)}</td>
                  <td className="px-3 py-3 text-right font-mono text-muted-foreground">{fmt(h.levels?.entry)}</td>
                  <td className="px-3 py-3 text-right font-mono text-bear">{fmt(h.levels?.stop_loss)}</td>
                  <td className="px-3 py-3 text-right font-mono text-bull">{fmt(h.levels?.tp1)}</td>
                  <td className={`px-3 py-3 text-right font-mono font-bold ${confColor(h.confluence_score)}`}>{h.confluence_score}</td>
                  <td className="px-3 py-3 text-muted-foreground text-xs whitespace-nowrap pr-6">{h.timeframe}</td>
                </tr>
              ))}
              {rows === null && <tr><td colSpan={12} className="px-6 py-12 text-center text-muted-foreground">Loading hot list…</td></tr>}
              {rows !== null && filtered.length === 0 && <tr><td colSpan={12} className="px-6 py-12 text-center text-muted-foreground">No signals match. Track an instrument or run the pipeline.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <AddInstrument open={showAdd} onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} />
    </>
  );
}
